import type { EmployeeWeeklySchedule } from '../types';
import { PAID_LUNCH_MINUTES, WORK_WEEKDAYS } from '../schedulingConfig';
import { isDayOff } from '../scheduleModel';

export {
  WORK_WEEKDAYS as WEEKDAYS,
  WEEKLY_METRICS,
  WEEKLY_DISPLAY_ROWS,
} from '../schedulingConfig';

export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/** Short total for dense shift cells (e.g. 7.5h). */
export function formatDurationCompact(minutes: number): string {
  if (minutes <= 0) return '0h';
  const hours = minutes / 60;
  if (Number.isInteger(hours)) return `${hours}h`;
  return `${Math.round(hours * 10) / 10}h`;
}

/**
 * Paid work minutes for a shift.
 * First {@link PAID_LUNCH_MINUTES} (0.5h) of lunch counts as work; only longer lunch is unpaid.
 */
export function calculateShiftTotalMinutes(entry: {
  start: string;
  end: string;
  lunchStart: string;
  lunchEnd: string;
}): number {
  const span = timeToMinutes(entry.end) - timeToMinutes(entry.start);
  if (span <= 0) return 0;

  const lunchRaw = timeToMinutes(entry.lunchEnd) - timeToMinutes(entry.lunchStart);
  const lunch = Number.isFinite(lunchRaw) ? Math.max(0, lunchRaw) : 0;
  const unpaidLunch = Math.max(0, lunch - PAID_LUNCH_MINUTES);

  return Math.max(0, span - unpaidLunch);
}

export function calculateWeeklyTotalMinutes(
  schedule: EmployeeWeeklySchedule | undefined
): number {
  if (!schedule) return 0;

  return WORK_WEEKDAYS.reduce((total, day) => {
    const entry = schedule.days[day];
    if (isDayOff(entry)) return total;
    return total + calculateShiftTotalMinutes(entry!);
  }, 0);
}
